import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../hooks/useAuth';

interface Resource {
  id: string;
  title: string;
  description: string;
  file_url: string;
  file_name: string;
  file_type: string;
  folder: string;
  subfolder?: string;
  grade: string;
  subject: string;
  created_at: string;
}

interface FolderViewProps {
  folderId: string;
  onBack: () => void;
  onRefresh?: () => void;
  onSubfolderChange?: (subfolder: string) => void;
}

export default function FolderView({ folderId, onBack, onRefresh, onSubfolderChange }: FolderViewProps) {
  const { isAdmin } = useAuth();
  const [resources, setResources] = useState<Resource[]>([]);
  const [subfolders, setSubfolders] = useState<string[]>([]);
  const [currentPath, setCurrentPath] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateSubfolder, setShowCreateSubfolder] = useState(false);
  const [newSubfolderName, setNewSubfolderName] = useState('');
  const [creatingSubfolder, setCreatingSubfolder] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteResourceConfirm, setDeleteResourceConfirm] = useState<string | null>(null);
  const [deletingResource, setDeletingResource] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingFolder, setDeletingFolder] = useState(false);

  // Password gate state
  const [showPasswordGate, setShowPasswordGate] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [pendingDeleteAction, setPendingDeleteAction] = useState<
    | { type: 'subfolder'; name: string }
    | { type: 'resource'; id: string }
    | { type: 'folder' }
    | null
  >(null);

  const ADMIN_PASSWORD = 'admin@mathandphysicsvault';

  const requestDelete = (
    action:
      | { type: 'subfolder'; name: string }
      | { type: 'resource'; id: string }
      | { type: 'folder' }
  ) => {
    setPendingDeleteAction(action);
    setPasswordInput('');
    setPasswordError('');
    setShowPasswordGate(true);
  };

  const handlePasswordConfirm = () => {
    if (passwordInput !== ADMIN_PASSWORD) {
      setPasswordError('Incorrect password. Please try again.');
      setPasswordInput('');
      return;
    }

    setShowPasswordGate(false);
    setPasswordInput('');
    setPasswordError('');

    if (!pendingDeleteAction) return;

    if (pendingDeleteAction.type === 'subfolder') {
      setDeleteConfirm(pendingDeleteAction.name);
    } else if (pendingDeleteAction.type === 'resource') {
      setDeleteResourceConfirm(pendingDeleteAction.id);
    } else if (pendingDeleteAction.type === 'folder') {
      setShowDeleteConfirm(true);
    }

    setPendingDeleteAction(null);
  };

  const getCurrentSubfolder = () => currentPath.join('/');

  const fetchResources = async () => {
    try {
      setLoading(true);
      setError('');
      
      console.log('Fetching resources for folder:', folderId, 'path:', currentPath);
      
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const response = await fetch(
        `${supabaseUrl}/functions/v1/get-resources-public?folder=${encodeURIComponent(folderId)}`
      );
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('API Response:', data);
      
      if (data.success) {
        const allResources = data.resources || [];
        const currentSubfolder = getCurrentSubfolder();
        
        // Get all subfolders at current level
        const currentLevel = currentPath.length;
        const uniqueSubfolders = new Set<string>();
        
        allResources.forEach((r: Resource) => {
          if (r.subfolder) {
            const parts = r.subfolder.split('/');
            
            // Check if this resource is in current path or deeper
            if (currentSubfolder) {
              // We're in a subfolder - show next level subfolders
              if (r.subfolder.startsWith(currentSubfolder + '/')) {
                const remainingPath = r.subfolder.substring(currentSubfolder.length + 1);
                const nextLevel = remainingPath.split('/')[0];
                if (nextLevel) {
                  uniqueSubfolders.add(nextLevel);
                }
              }
            } else {
              // We're at root - show first level subfolders
              if (parts.length > 0) {
                uniqueSubfolders.add(parts[0]);
              }
            }
          }
        });
        
        setSubfolders(Array.from(uniqueSubfolders));
        
        // Filter resources at current level (exact match, not deeper)
        const filteredResources = allResources.filter((r: Resource) => {
          if (currentSubfolder) {
            // Show resources that are exactly at this level (not in deeper subfolders)
            return r.subfolder === currentSubfolder && r.file_type !== 'folder';
          } else {
            // Show only root level resources (no subfolder or empty subfolder)
            return (!r.subfolder || r.subfolder === '') && r.file_type !== 'folder';
          }
        });
        
        setResources(filteredResources);
        
        console.log('Current path:', currentPath);
        console.log('Subfolders at this level:', Array.from(uniqueSubfolders));
        console.log('Resources at this level:', filteredResources.length);
      } else {
        throw new Error(data.error || 'Failed to load resources');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load resources');
      console.error('Error fetching resources:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSubfolder = async () => {
    if (!isAdmin()) {
      alert('Only administrators can create subfolders');
      return;
    }

    if (!newSubfolderName.trim()) {
      alert('Please enter a subfolder name');
      return;
    }

    if (subfolders.includes(newSubfolderName.trim())) {
      alert('A subfolder with this name already exists at this level');
      return;
    }

    try {
      setCreatingSubfolder(true);
      
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const currentSubfolder = getCurrentSubfolder();
      
      // Get auth token
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        alert('You must be logged in');
        return;
      }

      const response = await fetch(
        `${supabaseUrl}/functions/v1/create-subfolder`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ 
            folder: folderId,
            subfolder: newSubfolderName.trim(),
            parentPath: currentSubfolder || null
          }),
        }
      );

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to create subfolder');
      }

      await fetchResources();
      setShowCreateSubfolder(false);
      setNewSubfolderName('');
      
    } catch (error: any) {
      console.error('Create subfolder error:', error);
      alert(error.message || 'Failed to create subfolder');
    } finally {
      setCreatingSubfolder(false);
    }
  };

  const navigateToSubfolder = (subfolderName: string) => {
    const newPath = [...currentPath, subfolderName];
    setCurrentPath(newPath);
    if (onSubfolderChange) {
      onSubfolderChange(newPath.join('/'));
    }
  };

  const navigateUp = () => {
    if (currentPath.length > 0) {
      const newPath = currentPath.slice(0, -1);
      setCurrentPath(newPath);
      if (onSubfolderChange) {
        onSubfolderChange(newPath.join('/'));
      }
    } else {
      onBack();
    }
  };

  const navigateToLevel = (level: number) => {
    const newPath = currentPath.slice(0, level);
    setCurrentPath(newPath);
    if (onSubfolderChange) {
      onSubfolderChange(newPath.join('/'));
    }
  };

  useEffect(() => {
    fetchResources();
  }, [folderId, currentPath]);

  useEffect(() => {
    if (onRefresh) {
      fetchResources();
    }
  }, [onRefresh]);

  const handleDeleteSubfolder = async (subfolderName: string) => {
    if (!isAdmin()) {
      alert('Only administrators can delete subfolders');
      return;
    }

    try {
      setDeleting(true);
      
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const currentSubfolder = getCurrentSubfolder();
      
      const fullSubfolderPath = currentSubfolder 
        ? `${currentSubfolder}/${subfolderName}`
        : subfolderName;
      
      // Get auth token
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        alert('You must be logged in');
        return;
      }

      const response = await fetch(
        `${supabaseUrl}/functions/v1/delete-subfolder`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ 
            folder: folderId,
            subfolder: fullSubfolderPath
          }),
        }
      );

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to delete subfolder');
      }

      await fetchResources();
      setDeleteConfirm(null);
      
    } catch (error: any) {
      console.error('Delete subfolder error:', error);
      alert(error.message || 'Failed to delete subfolder');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteResource = async (resourceId: string) => {
    if (!isAdmin()) {
      alert('Only administrators can delete resources');
      return;
    }

    try {
      setDeletingResource(true);
      
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      
      // Get auth token
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        alert('You must be logged in');
        return;
      }

      const response = await fetch(
        `${supabaseUrl}/functions/v1/delete-resource`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ 
            resourceId: resourceId
          }),
        }
      );

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to delete resource');
      }

      await fetchResources();
      setDeleteResourceConfirm(null);
      
    } catch (error: any) {
      console.error('Delete resource error:', error);
      alert(error.message || 'Failed to delete resource');
    } finally {
      setDeletingResource(false);
    }
  };

  const handleDeleteFolder = async () => {
    if (!isAdmin()) {
      alert('Only administrators can delete folders');
      return;
    }

    try {
      setDeletingFolder(true);
      
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;
      
      // Get the auth token
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('You must be logged in to delete folders');
      }
      
      const response = await fetch(
        `${supabaseUrl}/functions/v1/delete-folder`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': supabaseAnonKey,
          },
          body: JSON.stringify({ 
            folderId: folderId
          }),
        }
      );

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to delete folder');
      }

      // Go back to main view and trigger refresh
      if (onRefresh) {
        onRefresh();
      }
      onBack();
      
    } catch (error: any) {
      console.error('Delete folder error:', error);
      alert(error.message || 'Failed to delete folder');
    } finally {
      setDeletingFolder(false);
      setShowDeleteConfirm(false);
    }
  };

  const getFileIcon = (fileType: string) => {
    if (fileType?.includes('pdf')) return 'ri-file-pdf-line text-red-500';
    if (fileType?.includes('word') || fileType?.includes('document')) return 'ri-file-word-line text-blue-500';
    if (fileType?.includes('sheet') || fileType?.includes('excel')) return 'ri-file-excel-line text-green-500';
    if (fileType?.includes('presentation') || fileType?.includes('powerpoint')) return 'ri-file-ppt-line text-orange-500';
    if (fileType?.includes('image')) return 'ri-image-line text-teal-500';
    if (fileType?.includes('video')) return 'ri-video-line text-purple-500';
    return 'ri-file-line text-gray-500';
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const handleFileClick = (resource: Resource) => {
    const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
    const fileUrl = `${supabaseUrl}/functions/v1/serve-file?id=${resource.id}#toolbar=0&navpanes=0&scrollbar=1`;
    window.open(fileUrl, '_blank', 'noopener,noreferrer');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-blue-600 animate-spin mb-4"></i>
          <p className="text-gray-600">Loading resources...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-20">
        <i className="ri-error-warning-line text-5xl text-red-500 mb-4"></i>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Resources</h3>
        <p className="text-gray-600 mb-6">{error}</p>
        <button
          onClick={onBack}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
        >
          Back to Folders
        </button>
      </div>
    );
  }

  const currentDisplayName = currentPath.length > 0 ? currentPath[currentPath.length - 1] : folderId;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with breadcrumb */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-4">
          <button
            onClick={navigateUp}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line text-xl text-gray-600"></i>
          </button>
          <div className="flex items-center gap-2 text-sm text-gray-600 flex-wrap">
            <button
              onClick={() => setCurrentPath([])}
              className={`hover:text-teal-600 transition-colors cursor-pointer ${currentPath.length === 0 ? 'font-semibold text-gray-900' : ''}`}
            >
              {folderId}
            </button>
            {currentPath.map((pathPart, index) => (
              <div key={index} className="flex items-center gap-2">
                <i className="ri-arrow-right-s-line"></i>
                <button
                  onClick={() => navigateToLevel(index + 1)}
                  className={`hover:text-teal-600 transition-colors cursor-pointer ${
                    index === currentPath.length - 1 ? 'font-semibold text-gray-900' : ''
                  }`}
                >
                  {pathPart}
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {currentDisplayName}
            </h2>
            <p className="text-sm text-gray-600">
              {resources.length} resources
              {subfolders.length > 0 && ` • ${subfolders.length} subfolders`}
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowCreateSubfolder(true)}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-folder-add-line"></i>
              <span>New Subfolder</span>
            </button>

            <button
              onClick={onBack}
              className="flex items-center text-gray-600 hover:text-gray-800 font-semibold transition-colors cursor-pointer whitespace-nowrap px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <i className="ri-arrow-left-line mr-2 text-lg"></i>
              Back to Folders
            </button>

            {isAdmin() && (
              <button
                onClick={() => requestDelete({ type: 'folder' })}
                className="flex items-center text-red-600 hover:text-red-700 font-semibold transition-colors cursor-pointer whitespace-nowrap px-4 py-2 border border-red-200 rounded-lg hover:bg-red-50"
              >
                <i className="ri-delete-bin-line mr-2 text-lg"></i>
                Delete Folder
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Subfolders Grid */}
      {subfolders.length > 0 && (
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Subfolders</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {subfolders.map((subfolder) => (
              <div key={subfolder} className="relative group">
                <button
                  onClick={() => navigateToSubfolder(subfolder)}
                  className="w-full bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6 hover:shadow-lg transition-all cursor-pointer text-left group"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="w-12 h-12 flex items-center justify-center bg-blue-500 rounded-lg group-hover:scale-110 transition-transform">
                      <i className="ri-folder-line text-white text-xl"></i>
                    </div>
                    <i className="ri-arrow-right-line text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity"></i>
                  </div>
                  <h4 className="font-semibold text-gray-900 truncate">{subfolder}</h4>
                </button>
                
                {isAdmin() && (
                  <button
                    onClick={() => requestDelete({ type: 'subfolder', name: subfolder })}
                    className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center bg-red-500 text-white rounded-full transition-opacity hover:bg-red-600 cursor-pointer"
                    title="Delete folder"
                  >
                    <i className="ri-delete-bin-line text-sm"></i>
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Resources Grid */}
      {resources.length === 0 && subfolders.length === 0 ? (
        <div className="text-center py-20 bg-gray-50 rounded-xl">
          <i className="ri-folder-open-line text-5xl text-gray-400 mb-4"></i>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No Resources Yet</h3>
          <p className="text-gray-600">Upload your first resource or create a subfolder</p>
        </div>
      ) : resources.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Resources</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {resources.map((resource) => (
              <div
                key={resource.id}
                className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow relative group"
              >
                <button
                  onClick={() => requestDelete({ type: 'resource', id: resource.id })}
                  className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center bg-red-500 text-white rounded-full hover:bg-red-600 cursor-pointer"
                  title="Delete resource"
                >
                  <i className="ri-delete-bin-line text-sm"></i>
                </button>

                <div
                  onClick={() => handleFileClick(resource)}
                  className="flex items-start mb-4 cursor-pointer"
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-gray-100 rounded-lg hover:bg-blue-50 transition-colors">
                    <i className={`${getFileIcon(resource.file_type)} text-2xl`}></i>
                  </div>
                </div>

                <div
                  onClick={() => handleFileClick(resource)}
                  className="w-full text-left cursor-pointer"
                >
                  <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2 hover:text-blue-600 transition-colors">
                    {resource.title}
                  </h3>

                  {resource.description && (
                    <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                      {resource.description}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  {resource.subject && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <i className="ri-book-line"></i>
                      <span>{resource.subject}</span>
                    </div>
                  )}
                  {resource.grade && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <i className="ri-graduation-cap-line"></i>
                      <span>{resource.grade}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <i className="ri-calendar-line"></i>
                    <span>{formatDate(resource.created_at)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Admin Password Gate Modal ── */}
      {showPasswordGate && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full p-8 shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-keyhole-line text-3xl text-red-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-1">Admin Confirmation</h3>
              <p className="text-sm text-gray-500">Enter the admin password to confirm this delete action.</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Admin Password</label>
                <input
                  type="password"
                  value={passwordInput}
                  onChange={(e) => {
                    setPasswordInput(e.target.value);
                    setPasswordError('');
                  }}
                  onKeyDown={(e) => e.key === 'Enter' && handlePasswordConfirm()}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400 text-sm ${
                    passwordError ? 'border-red-400 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="Enter admin password"
                  autoFocus
                />
                {passwordError && (
                  <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                    <i className="ri-error-warning-line"></i>
                    {passwordError}
                  </p>
                )}
              </div>

              <div className="flex gap-3 pt-1">
                <button
                  onClick={() => {
                    setShowPasswordGate(false);
                    setPasswordInput('');
                    setPasswordError('');
                    setPendingDeleteAction(null);
                  }}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePasswordConfirm}
                  className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors cursor-pointer whitespace-nowrap text-sm font-medium"
                >
                  Confirm Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Subfolder Modal */}
      {showCreateSubfolder && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowCreateSubfolder(false);
              setNewSubfolderName('');
            }
          }}
        >
          <div className="bg-white rounded-xl max-w-md w-full p-6 relative">
            <button
              onClick={() => {
                setShowCreateSubfolder(false);
                setNewSubfolderName('');
              }}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 cursor-pointer transition-colors"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>

            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-folder-add-line text-3xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Create New Subfolder</h3>
              <p className="text-sm text-gray-600">
                in {currentPath.length > 0 ? currentPath.join(' / ') : folderId}
              </p>
            </div>

            <form onSubmit={(e) => {
              e.preventDefault();
              handleCreateSubfolder();
            }} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subfolder Name
                </label>
                <input
                  type="text"
                  value={newSubfolderName}
                  onChange={(e) => setNewSubfolderName(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter subfolder name"
                  autoFocus
                  disabled={creatingSubfolder}
                />
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateSubfolder(false);
                    setNewSubfolderName('');
                  }}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                  disabled={creatingSubfolder}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center justify-center gap-2"
                  disabled={creatingSubfolder || !newSubfolderName.trim()}
                >
                  {creatingSubfolder ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      <span>Creating...</span>
                    </>
                  ) : (
                    <>
                      <i className="ri-add-line"></i>
                      <span>Create</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Subfolder Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 flex items-center justify-center bg-red-100 rounded-full mr-4">
                <i className="ri-alert-line text-2xl text-red-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete Folder</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete the folder "<strong>{deleteConfirm}</strong>" and all its contents? This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteSubfolder(deleteConfirm)}
                disabled={deleting}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer"
              >
                {deleting ? 'Deleting...' : 'Delete Folder'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Resource Confirmation Modal */}
      {deleteResourceConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 flex items-center justify-center bg-red-100 rounded-full mr-4">
                <i className="ri-alert-line text-2xl text-red-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete Resource</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this resource? This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteResourceConfirm(null)}
                disabled={deletingResource}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteResource(deleteResourceConfirm)}
                disabled={deletingResource}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer"
              >
                {deletingResource ? 'Deleting...' : 'Delete Resource'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Folder Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 flex items-center justify-center bg-red-100 rounded-full mr-4">
                <i className="ri-alert-line text-2xl text-red-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete Folder</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this folder and all its contents? This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deletingFolder}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteFolder}
                disabled={deletingFolder}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer"
              >
                {deletingFolder ? 'Deleting...' : 'Delete Folder'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
